import React from "react";
import { FieldArray } from "formik";
import {
  SectionToggleState,
  useBoolean,
  useFieldValue,
  useSectionHelper,
} from "hooks";
import { FinanceSubModalSection } from "constants/settings";
import { CollapseHeader, FormCollapse } from "components/Form";
import { withNamespace } from "components/Form/FormikControls";
import { Box, Grid } from "@material-ui/core";
import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Paper } from "components/Paper";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { AuditLogTypes } from "constants/auditLog";
import { BankingDetailsForm } from "components/Form/Fields/BankingDetailsForm";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

interface PaymentBankingDetailsProps {
  sectionHelper: SectionToggleState;
  namespace: string;
}

interface BankingDetailProps {
  index: number;
  namespace: string;
  removeRow: (index: number) => null;
  id: string;
}

function BankingDetail(props: BankingDetailProps) {
  const { index, removeRow, id, namespace } = props;
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useBoolean();

  return (
    <Grid item xs={12} key={id || index}>
      <Paper>
        <Box display="flex" justifyContent="flex-end" gridGap="4px">
          <SlimButton type="button">
            <AuditLogLink
              auditLogType={AuditLogTypes.BANKING_DETAIL}
              dataId={id}
              returnTo={window.location.hash}
            />
          </SlimButton>
          <SlimButton
            onClick={openConfirmDialog}
            type="button"
            backgroundColor="deleteRed"
          >
            <ButtonIcon icon={faTrash} />
          </SlimButton>
          <ConfirmDialog
            title="Are you sure?"
            isOpen={isConfirmDialogOpen}
            onCancel={closeConfirmDialog}
            onDelete={() => removeRow(index)}
          />
        </Box>
        <Grid item xs={12} spacing={2} container>
          <BankingDetailsForm
            namespace={withNamespace(
              namespace,
              `paymentBankingDetails.${index}`,
            )}
            includeHobbyFarmer={false}
          />
        </Grid>
      </Paper>
    </Grid>
  );
}

export const BankingDetails: React.FC = ({
  sectionHelper,
  namespace: ns,
}: PaymentBankingDetailsProps) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.PAYMENT_BANKING_DETAILS,
  );

  const paymentBankingDetails =
    useFieldValue(withNamespace(ns, "paymentBankingDetails")) || [];
  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={<CollapseHeader title="Banking Details" />}
    >
      <FieldArray name={withNamespace(ns, "paymentBankingDetails")}>
        {arrayHelpers => {
          const addNew = () => {
            arrayHelpers.push({
              bankingDetails: { accountName: "", accountNumber: "", bsb: "" },
              saleFilters: [],
              isDefault: false,
            });
          };

          return (
            <>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                  <SlimButton onClick={addNew} type="button">
                    <ButtonIcon icon={faPlus} />
                    &nbsp;New Banking Details
                  </SlimButton>
                  <Tooltip title="Add bank accounts." />
                </Box>
              </Grid>
              {paymentBankingDetails.map(
                (paymentBankingDetail: BankingDetails, index) => (
                  <BankingDetail
                    key={paymentBankingDetail.id || index}
                    namespace={ns}
                    index={index}
                    id={paymentBankingDetail.id}
                    removeRow={arrayHelpers.remove}
                  />
                ),
              )}
            </>
          );
        }}
      </FieldArray>
    </FormCollapse>
  );
};
